import { authService } from './authService';

// Asegúrate de que esta URL base sea la correcta para tu entorno actual
// (HTTP a la IP:Puerto para depuración, o HTTPS a tu dominio en producción)
const API_BASE_URL = process.env.API_BASE_URL || 'http://68.168.218.177:5000/api';

/**
 * Función genérica para hacer solicitudes HTTP.
 * @param {string} urlPath - La parte de la URL después de la BASE_URL (ej. '/auth/login').
 * @param {string} method - El método HTTP (GET, POST, PUT, DELETE).
 * @param {Object} [body=null] - El cuerpo de la solicitud para POST/PUT/DELETE.
 * @param {Object} [params=null] - Parámetros de consulta para solicitudes GET.
 * @returns {Promise<Object>} La respuesta JSON de la API.
 * @throws {Error} Si la respuesta de la red no es OK o si la API devuelve un error.
 */
async function httpRequest(urlPath, method, body = null, params = null) {
    const token = authService.getToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const options = {
        method: method,
        headers: headers,
    };

    // Construir la URL completa, incluyendo los parámetros de consulta si existen
    let url = `${API_BASE_URL}${urlPath}`;
    if (params) {
        const queryString = new URLSearchParams(params).toString();
        url = `${url}?${queryString}`;
    }

    if (body) {
        options.body = JSON.stringify(body);
    }
    
    console.log(`httpRequest: Realizando ${method} a ${url}`);

    try {
        const response = await fetch(url, options);

        if (response.status === 204) {
            console.log(`httpRequest: Respuesta recibida para ${method} ${urlPath}, status: 204`);
            return {};
        }

        const contentType = response.headers.get('content-type');
        let data = {};
        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
            console.log(`httpRequest: Datos JSON recibidos para ${urlPath}:`, data);
        } else {
            const text = await response.text();
            console.warn(`httpRequest: Respuesta no JSON para ${urlPath}, status: ${response.status}. Contenido:`, text);
            data = { message: text, status: response.status };
        }
        
        if (!response.ok) {
            console.error(`httpRequest: Respuesta no OK, status: ${response.status}`);
            console.error(`httpRequest: Detalles del error del backend:`, data);
            
            // ⭐ MODIFICACIÓN CLAVE: Lanzar un nuevo error con el mensaje del backend
            const errorMsg = data.error || data.message || `Error del servidor: ${response.statusText}`;
            throw new Error(errorMsg);
        }

        console.log(`httpRequest: Respuesta recibida para ${method} ${urlPath}, status: ${response.status}`);
        return data;

    } catch (error) {
        console.error(`API Error on ${method} ${urlPath}:`, error);
        // ⭐ MODIFICACIÓN CLAVE: Si el error ya es un objeto Error con un mensaje, lo relanzamos
        if (error instanceof Error) {
            throw error;
        }
        // De lo contrario, creamos un nuevo error más descriptivo
        throw new Error(`Error de red o desconocido: ${error.message}`);
    }
}

export const api = {
    get: (urlPath, params) => httpRequest(urlPath, 'GET', null, params),
    post: (urlPath, body) => httpRequest(urlPath, 'POST', body),
    put: (urlPath, body) => httpRequest(urlPath, 'PUT', body),
    delete: (urlPath) => httpRequest(urlPath, 'DELETE'),

    sendWhatsAppNotification: async (ticketCode, phoneNumber, ticketData) => {
        try {
            const result = await httpRequest('/notifications/send-whatsapp', 'POST', { ticketCode, phoneNumber, ticketData });
            console.log('sendWhatsAppNotification: Resultado recibido:', result);
            return result;
        } catch (error) {
            console.error('sendWhatsAppNotification: Error al enviar mensaje de WhatsApp:', error);
            throw error;
        }
    },

    // Nuevos métodos para interactuar con la nueva API
    getUpcomingRaces: () => httpRequest('/races/upcoming', 'GET'),

    getRaceResults: (params) => httpRequest('/races/results', 'GET', null, params),

    getBetTypes: () => httpRequest('/bet_types', 'GET'),
    
    getHorsesInRace: (raceId) => httpRequest(`/races/${raceId}/horses`, 'GET'),

    getCashierConfig: () => httpRequest('/configs/cashier', 'GET'),

    requestValidationToken: (phoneNumber) => httpRequest('/configs/request-token', 'POST', phoneNumber),

    updateCashierConfig: (updates) => httpRequest('/configs/cashier', 'PUT', updates),

    getTickets: (params) => httpRequest('/tickets', 'GET', null, params),

    getLastTicketByCashier: (cashierId) => httpRequest(`/tickets/last-by-cashier/${cashierId}`, 'GET'),
    
    getTicketById: (ticketId) => httpRequest(`/tickets/${ticketId}`, 'GET'),

    sellTicket: (payload) => httpRequest('/tickets/sell', 'POST', payload),

    voidTicket: (id) => httpRequest(`/tickets/${id}/void`, 'PUT', null),

    scanTicket: (ticket_code) => httpRequest(`/tickets/scan/${ticket_code}`, 'GET'),

    payTicket: (id) => httpRequest(`/tickets/pay/${id}`, 'POST'),

    getCashReconciliation: (params) => httpRequest('/reports/cash-reconciliation', 'GET', null, params),
};
